<template>
  <button
    @click="$emit('click')"
    :disabled="disabled"
    :class="buttonClasses"
    class="group transition-all duration-200 flex flex-col items-center space-y-2 min-w-[200px]"
  >
    <!-- Icon -->
    <div class="flex-shrink-0">
      <div :class="iconContainerClasses">
        <svg class="w-8 h-8" :class="iconClasses" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <slot name="icon">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </slot>
        </svg>
      </div>
    </div>
    
    <!-- Text Content -->
    <div class="text-center">
      <span class="font-semibold block">{{ title }}</span>
      <span class="text-sm opacity-90 block">{{ subtitle }}</span>
    </div>
  </button>
</template>

<script>
export default {
  name: 'ActionButton',
  emits: ['click'],
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'primary', // 'primary', 'secondary', 'success', 'warning', 'danger', 'info'
      validator: value => ['primary', 'secondary', 'success', 'warning', 'danger', 'info'].includes(value)
    },
    size: {
      type: String,
      default: 'lg', // 'sm', 'md', 'lg'
      validator: value => ['sm', 'md', 'lg'].includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    buttonClasses() {
      const baseClasses = 'rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2'
      const sizeClasses = {
        sm: 'px-4 py-2 text-sm',
        md: 'px-6 py-3 text-base',
        lg: 'px-8 py-4 text-base'
      }
      const variantClasses = {
        primary: 'bg-blue-50 text-gray-800 hover:bg-blue-100 focus:ring-blue-500 border-2 border-blue-400',
        secondary: 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus:ring-gray-500 border border-gray-400',
        success: 'bg-green-50 text-gray-800 hover:bg-green-100 focus:ring-green-500 border-2 border-green-400',
        warning: 'bg-yellow-50 text-gray-800 hover:bg-yellow-100 focus:ring-yellow-500 border border-yellow-400',
        danger: 'bg-red-50 text-gray-800 hover:bg-red-100 focus:ring-red-500 border border-red-400',
        info: 'bg-indigo-50 text-gray-800 hover:bg-indigo-100 focus:ring-indigo-500 border border-indigo-400'
      }
      
      const disabledClasses = this.disabled 
        ? 'opacity-50 cursor-not-allowed' 
        : ''
      
      return [
        baseClasses,
        sizeClasses[this.size],
        variantClasses[this.variant],
        disabledClasses
      ].join(' ')
    },
    iconContainerClasses() {
      const baseClasses = 'w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-200'
      const variantClasses = {
        primary: 'bg-blue-50 group-hover:bg-blue-500',
        secondary: 'bg-gray-50 group-hover:bg-gray-500',
        success: 'bg-green-50 group-hover:bg-green-500',
        warning: 'bg-yellow-50 group-hover:bg-yellow-400',
        danger: 'bg-red-50 group-hover:bg-red-400',
        info: 'bg-indigo-50 group-hover:bg-indigo-400'
      }
      
      return [
        baseClasses,
        variantClasses[this.variant]
      ].join(' ')
    },
    iconClasses() {
      const variantClasses = {
        primary: 'text-blue-600 group-hover:text-white',
        secondary: 'text-gray-600 group-hover:text-white',
        success: 'text-green-600 group-hover:text-white',
        warning: 'text-yellow-600 group-hover:text-white',
        danger: 'text-red-600 group-hover:text-white',
        info: 'text-indigo-600 group-hover:text-white'
      }
      
      return variantClasses[this.variant]
    }
  }
}
</script>
